import { StoreApi } from 'zustand'
import { preferencesDataActions } from '@/scopes/checkout/domain/actions/preferencesFormData'
import {
  ParticipantFormDataAndTermsAndConditions,
  ParticipantsFormStepDataClient,
} from '@/scopes/checkout/domain/types/checkoutPersonalDetails'
import { PreferencesFormData } from '@/scopes/checkout/domain/types/checkoutPreferences'
import {
  ParticipantFormStepStore,
} from '@/scopes/checkout/infrastructure/store/participants-form-step-store'
import { Logger } from '@/scopes/common/domain/types'
import { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import { getMandatoryPaymentAbTestUdsUrl, getMandatoryPaymentAbTestUrl } from '../../policies/mandatoryPaymentAbTest'

export interface ParticipantFormDataActionsDependencies {
  store: StoreApi<ParticipantFormStepStore>
  client: ParticipantsFormStepDataClient
  logger: Logger
  preferenceActions: ReturnType<typeof preferencesDataActions>
  location: LocationResolver
}

export interface ParticipantsFormDataSubmittedPayload {
  sid: string
  participantFormData: ParticipantFormDataAndTermsAndConditions
  preferences: PreferencesFormData | null
}

export interface ParticipantFormDataActions {
  pageLoaded: (payload: { sid: string }) => Promise<void>
  participantsFormDataSubmitted: (payload: ParticipantsFormDataSubmittedPayload) => Promise<{ success: boolean }>
  closeConfirmActionWindow: () => void
  redirectToNextStep: () => void
  redirectToMandatoryPaymentStepSZabTest: () => void
  redirectToMandatoryPaymentStepUds: () => void
}

export function participantsFormDataActions(
  {
    store,
    client,
    logger,
    preferenceActions,
    location,
  }:
  ParticipantFormDataActionsDependencies,
): ParticipantFormDataActions {
  async function pageLoaded({ sid }: { sid: string }) {
    const state = store.getState()

    try {
      const { participantDetails: { fields, phoneCountries, postalCodeCountries }, bookingDraft } = await client.getParticipantsDetailsFormData(sid)
      state.setIn('participantsByRooms', fields)
      state.setIn('availablePhoneCountries', phoneCountries)
      state.setIn('availablePostalCodeCountries', postalCodeCountries)
      state.setIn('nextStepUrl', `${bookingDraft.navigation.nextStep.url}?sid=${sid}`)
    }
    catch (error) {
      state.setIn('fetchingError', true)

      logger.logError(error as Error, { action: 'pageLoaded', sid })
    }
    state.setIn('isFetched', true)
  }

  async function participantsFormDataSubmitted({
    sid,
    participantFormData,
    preferences,
  }: ParticipantsFormDataSubmittedPayload): Promise<{ success: boolean }> {
    const state = store.getState()
    state.setIn('isSubmitting', true)

    let success = false
    try {
      const { termsAndConditions, ...participantFormDataFiltered } = participantFormData

      await client.submitParticipantsFormData(sid, participantFormDataFiltered)
      await preferenceActions.preferencesDataSubmitted({ sid, preferencesData: preferences })

      state.setIn('confirmActionWindowIsOpen', true)
      success = true
    }
    catch (error) {
      state.setIn('submissionError', true)

      logger.logError(error as Error, { action: 'participantsFormDataSubmitted', sid })
      success = false
    }
    finally {
      state.setIn('isSubmitting', false)
    }

    return { success }
  }

  function closeConfirmActionWindow() {
    const state = store.getState()
    state.setIn('confirmActionWindowIsOpen', false)
  }

  function redirectToNextStep() {
    const state = store.getState()
    location.redirectTo(state.nextStepUrl)
  }

  function redirectToMandatoryPaymentStepSZabTest() {
    const state = store.getState()

    let nextStepUrl = state.nextStepUrl

    nextStepUrl = getMandatoryPaymentAbTestUrl(nextStepUrl)

    location.redirectTo(nextStepUrl)
  }

  function redirectToMandatoryPaymentStepUds() {
    const state = store.getState()
    location.redirectTo(getMandatoryPaymentAbTestUdsUrl(state.nextStepUrl))
  }

  return {
    redirectToMandatoryPaymentStepUds,
    pageLoaded,
    participantsFormDataSubmitted,
    closeConfirmActionWindow,
    redirectToNextStep,
    redirectToMandatoryPaymentStepSZabTest,
  }
}
